import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { format } from 'date-fns'

import useTableSort from '@/hooks/table/use-table-sort.hook'
import { transactionsSelector } from '@/state/transactions/slices/index.slices'
import { uiGlobalSelector } from '@/state/ui/global/slices/index.slices'

import { formatRowDate, getDateTimeFromIsoUTC } from '@/utils/date'
import { BonusType, CurrencyCodeEnum, TimeZoneUTCType } from '@/enums'
import { getFormatNumber } from '@/utils'

import { TableHeadCellPropsType } from '@/state/ui/global/models'
import {
  TransactionWalletsLibraryColumnProps,
  Transactions,
} from '@/state/transactions/models'
import { dateTimeTableFormat } from '@/constants/date'

export const useTransactions = () => {
  const { t } = useTranslation()

  const { transactions, pagination, isLoading, filters } =
    useSelector(transactionsSelector)

  const { isLoadingFull } = useSelector(uiGlobalSelector)

  let headCells: TableHeadCellPropsType<TransactionWalletsLibraryColumnProps>[] =
    [
      { id: 'dateAtText', label: t('labels.date'), width: 120, sort: true },
      { id: 'id', label: t('transaction.transactionId'), sort: true },
      {
        id: 'transactionType',
        label: t('transaction.transactionTypeLabel'),
        sort: true,
      },
      { id: 'gameProvider', label: t('labels.gameProvider'), sort: true },
      { id: 'gameCategory', label: t('labels.gameCategory'), sort: true },
      { id: 'game', label: t('labels.game'), sort: true },
      { id: 'amountNumber', label: t('labels.amount'), sort: true },
      {
        id: 'walletBalanceAfter',
        label: t('labels.walletBalanceAfter'),
        sort: true,
      },
      {
        id: 'rolloverCont',
        label: t('labels.rolloverContribution'),
        sort: true,
      },
      {
        id: 'currentRollover',
        label: t('transaction.currentRollover'),
        sort: true,
      },
    ]

  // if (filters.walletType === BonusType.FREE_PLAY) {
  //   headCells = [
  //     { id: 'dateAtText', label: t('labels.date'), width: 120, sort: true },
  //     { id: 'id', label: t('transaction.transactionId'), sort: true },
  //     { id: 'status', label: t('transaction.sportWagerStatus'), sort: true },
  //     { id: 'betType', label: t('transaction.sportWagerType'), sort: true },
  //     { id: 'riskAmount', label: t('transaction.riskAmount'), sort: true },
  //     { id: 'winAmount', label: t('transaction.winAmount'), sort: true },
  //     {
  //       id: 'walletBalanceAfter',
  //       label: t('labels.walletBalanceAfter'),
  //       sort: true,
  //     },
  //     {
  //       id: 'sportBetAmount',
  //       label: t('transaction.sportBetAmount'),
  //       sort: true,
  //     },
  //   ]
  // }

  const formattedTransactions =
    transactions?.map((transaction) => {
      return {
        ...transaction,
        date: transaction.date ? formatRowDate(transaction.date) : '-',
        dateAtText: transaction.date
          ? new Date(transaction.date).getTime()
          : '-',
        amountNumber: transaction.amount,
        amountText: getFormatNumber(
          transaction.amount,
          transaction.currency || CurrencyCodeEnum.USD,
        ),
        walletBalanceAfter: transaction.wallets[0].walletBalanceAfter,
        walletBalanceAfterText: getFormatNumber(
          transaction.wallets[0].walletBalanceAfter,
          transaction.currency || CurrencyCodeEnum.USD,
        ),
        sportBetAmount: transaction.wallets[0].sportBetAmount,
        sportBetAmountText: getFormatNumber(
          transaction.wallets[0].sportBetAmount,
          transaction.currency || CurrencyCodeEnum.USD,
        ),
        rolloverCont: transaction.wallets[0].rolloverCont,
        rolloverContText: getFormatNumber(
          transaction.wallets[0].rolloverCont,
          transaction.currency || CurrencyCodeEnum.USD,
        ),
        currentRollover: transaction.wallets[0].currentRollover,
        currentRolloverText: getFormatNumber(
          transaction.wallets[0].currentRollover,
          transaction.currency || CurrencyCodeEnum.USD,
        ),
      }
    }) || []

  const sortedInitialState = 'id'

  const { sortedRows, createSortHandler, order, orderBy } = useTableSort<
    TransactionWalletsLibraryColumnProps,
    keyof TransactionWalletsLibraryColumnProps
  >(formattedTransactions, sortedInitialState, 'desc')

  const getXlsFormattedRow = (
    transaction: Transactions,
  ): TransactionWalletsLibraryColumnProps => {
    return {
      ...transaction,
      date: transaction.date ? formatRowDate(transaction.date) : '-',
      dateAtText: transaction.date
        ? `${format(
            getDateTimeFromIsoUTC(
              transaction.date,
              TimeZoneUTCType['GMT-07 Pacific Time'],
            ),
            dateTimeTableFormat,
          )} PT`
        : '-',
      transactionType:
        transaction.transactionType === 1
          ? t('transaction.transactionType.WIN')
          : t('transaction.transactionType.BET'),
      gameProvider: transaction.gameProviderName,
      gameCategory: transaction.gameCategoryName,
      game: transaction.gameName,
      amountNumber: getFormatNumber(
        transaction.amount,
        transaction.currency || CurrencyCodeEnum.USD,
      ),
      walletBalanceAfter: getFormatNumber(
        transaction.wallets[0].walletBalanceAfter,
        transaction.currency || CurrencyCodeEnum.USD,
      ),
      rolloverCont: getFormatNumber(
        transaction.wallets[0].rolloverCont,
        transaction.currency || CurrencyCodeEnum.USD,
      ),
      sportBetAmount: getFormatNumber(
        transaction.wallets[0].sportBetAmount,
        transaction.currency || CurrencyCodeEnum.USD,
      ),
      currentRollover: getFormatNumber(
        transaction.wallets[0].currentRollover,
        transaction.currency || CurrencyCodeEnum.USD,
      ),
      status: t(`transaction.sportWagerStatusOptions.${transaction.status}`),
      betType: t(`transaction.sportWagerTypeOptions.${transaction.betType}`),
    }
  }

  return {
    sortedRows,
    createSortHandler,
    order,
    orderBy,
    pagination,
    isLoading,
    isLoadingFull,
    headCells,
    getXlsFormattedRow,
  }
}
